import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Edit2, Type, DollarSign, Save, LogOut, Check, ChevronRight, UtensilsCrossed, Image, Search, X, Palette, Monitor } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translations for mobile editor
const mobileTranslations = {
  selectMenu: {
    en: 'Select Menu',
    es: 'Seleccionar Menú',
    pt: 'Selecionar Menu',
    ht: 'Chwazi Meni'
  },
  menuCount: {
    en: 'menu(s)',
    es: 'menú(s)',
    pt: 'menu(s)',
    ht: 'meni'
  },
  texts: {
    en: 'texts',
    es: 'textos',
    pt: 'textos',
    ht: 'tèks'
  },
  noMenusCreated: {
    en: "You don't have any menus created",
    es: 'No tienes menús creados',
    pt: 'Você não tem menus criados',
    ht: 'Ou pa gen meni kreye'
  },
  createMenuFromComputer: {
    en: 'Create a menu from the computer',
    es: 'Crea un menú desde la computadora',
    pt: 'Crie um menu no computador',
    ht: 'Kreye yon meni nan òdinatè a'
  },
  save: {
    en: 'Save',
    es: 'Guardar',
    pt: 'Salvar',
    ht: 'Anrejistre'
  },
  searchPlaceholder: {
    en: 'Search product or price...',
    es: 'Buscar producto o precio...',
    pt: 'Buscar produto ou preço...',
    ht: 'Chèche pwodwi oswa pri...'
  },
  resultsOf: {
    en: 'of',
    es: 'de',
    pt: 'de',
    ht: 'sou'
  },
  results: {
    en: 'results',
    es: 'resultados',
    pt: 'resultados',
    ht: 'rezilta'
  },
  name: {
    en: 'Name',
    es: 'Nombre',
    pt: 'Nome',
    ht: 'Non'
  },
  price: {
    en: 'Price',
    es: 'Precio',
    pt: 'Preço',
    ht: 'Pri'
  },
  noResultsFound: {
    en: 'No results found',
    es: 'No se encontraron resultados',
    pt: 'Nenhum resultado encontrado',
    ht: 'Pa gen rezilta jwenn'
  },
  tryAnotherSearch: {
    en: 'Try another search',
    es: 'Intenta con otra búsqueda',
    pt: 'Tente outra busca',
    ht: 'Eseye yon lòt rechèch'
  },
  clearSearch: {
    en: 'Clear search',
    es: 'Limpiar búsqueda',
    pt: 'Limpar busca',
    ht: 'Efase rechèch'
  },
  noTextsInMenu: {
    en: 'No texts in this menu',
    es: 'No hay textos en este menú',
    pt: 'Não há textos neste menu',
    ht: 'Pa gen tèks nan meni sa a'
  },
  addTextsFromEditor: {
    en: 'Add texts from the editor on computer',
    es: 'Agrega textos desde el editor en computadora',
    pt: 'Adicione textos no editor do computador',
    ht: 'Ajoute tèks nan editè sou òdinatè a'
  },
  edit: {
    en: 'Edit',
    es: 'Editar',
    pt: 'Editar',
    ht: 'Modifye'
  },
  productName: {
    en: 'Product name',
    es: 'Nombre del producto',
    pt: 'Nome do produto',
    ht: 'Non pwodwi a'
  },
  cancel: {
    en: 'Cancel',
    es: 'Cancelar',
    pt: 'Cancelar',
    ht: 'Anile'
  },
  changesWillReflect: {
    en: 'Changes will be reflected in the public view after saving',
    es: 'Los cambios se reflejarán en la vista pública después de guardar',
    pt: 'As alterações serão refletidas na visualização pública após salvar',
    ht: 'Chanjman yo ap parèt nan vi piblik la apre anrejistreman'
  }
};

const MobileEditPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Helper function to get mobile translations
  const mt = (key) => mobileTranslations[key]?.[language] || mobileTranslations[key]?.en || key;
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Mode selection state (new)
  const [selectedMode, setSelectedMode] = useState(null); // 'editor' or 'prices'
  
  // Menu selection state
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [isLoadingMenus, setIsLoadingMenus] = useState(true);
  
  // Edit state
  const [isSaving, setIsSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState("");
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Verify token on load
  useEffect(() => {
    const verifyAuth = async () => {
      if (token) {
        try {
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
        } catch {
          localStorage.removeItem("token");
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      setIsLoadingMenus(false);
    };
    verifyAuth();
  }, [token]);

  // Fetch menus when authenticated
  const fetchMenus = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    
    setIsLoadingMenus(true);
    try {
      const response = await axios.get(`${API}/menus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenus(response.data);
    } catch (error) {
      toast.error(t('loading') === 'Loading...' ? "Error loading menus" : "Error al cargar los menús");
    } finally {
      setIsLoadingMenus(false);
    }
  }, [token, isAuthenticated, t]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMenus();
    }
  }, [isAuthenticated, fetchMenus]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken } = response.data;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      toast.success(t('welcomeBack'));
    } catch (error) {
      const errorMsg = {
        en: "Invalid credentials",
        es: "Credenciales incorrectas",
        pt: "Credenciais inválidas",
        ht: "Idantifyan pa valid"
      };
      toast.error(errorMsg[language] || errorMsg.en);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setIsAuthenticated(false);
    setMenus([]);
    setSelectedMenu(null);
  };

  const handleSelectMenu = (menu) => {
    setSelectedMenu(menu);
    setSearchQuery(""); // Clear search when selecting a menu
  };

  const handleBackToMenus = () => {
    setSelectedMenu(null);
    setSearchQuery(""); // Clear search when going back
  };

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!selectedMenu?.items) return [];
    if (!searchQuery.trim()) return selectedMenu.items;
    
    const query = searchQuery.toLowerCase().trim();
    return selectedMenu.items.filter(item => {
      const label = (item.label || "").toLowerCase();
      const text = (item.text || "").toLowerCase();
      return label.includes(query) || text.includes(query);
    });
  }, [selectedMenu?.items, searchQuery]);

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditText(item.text);
  };

  const handleSaveItem = async () => {
    if (!editingItem || !selectedMenu) return;

    // Update locally
    const updatedMenu = {
      ...selectedMenu,
      items: selectedMenu.items.map(item =>
        item.id === editingItem.id
          ? { ...item, text: editText }
          : item
      )
    };
    
    setSelectedMenu(updatedMenu);
    setMenus(prev => prev.map(m => m.id === selectedMenu.id ? updatedMenu : m));

    setEditingItem(null);
    setEditText("");
    const successMsg = {
      en: "Text updated",
      es: "Texto actualizado",
      pt: "Texto atualizado",
      ht: "Tèks mete ajou"
    };
    toast.success(successMsg[language] || successMsg.en);
  };

  const handleSaveAll = async () => {
    if (!selectedMenu) return;
    setIsSaving(true);

    try {
      await axios.put(
        `${API}/menus/${selectedMenu.id}`,
        {
          name: selectedMenu.name,
          background_image: selectedMenu.background_image,
          items: selectedMenu.items,
          canvas_width: selectedMenu.canvas_width,
          canvas_height: selectedMenu.canvas_height
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('menuSaved'));
    } catch (error) {
      const errorMsg = {
        en: "Error saving",
        es: "Error al guardar",
        pt: "Erro ao salvar",
        ht: "Erè nan anrejistreman"
      };
      toast.error(errorMsg[language] || errorMsg.en);
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoadingMenus && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-stone-600">{t('loading')}</div>
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    const getLoginTitle = () => {
      switch(language) {
        case 'en': return 'Edit Menu';
        case 'pt': return 'Editar Menu';
        case 'ht': return 'Modifye Meni';
        default: return 'Editar Menú';
      }
    };
    
    const getLoginSubtitle = () => {
      switch(language) {
        case 'en': return 'Enter your credentials';
        case 'pt': return 'Digite suas credenciais';
        case 'ht': return 'Antre idantifyan ou';
        default: return 'Ingresa tus credenciales';
      }
    };
    
    const getLoginBtnText = () => {
      switch(language) {
        case 'en': return isLoggingIn ? 'Signing in...' : 'Sign In';
        case 'pt': return isLoggingIn ? 'Entrando...' : 'Entrar';
        case 'ht': return isLoggingIn ? 'Konekte...' : 'Konekte';
        default: return isLoggingIn ? 'Ingresando...' : 'Ingresar';
      }
    };
    
    return (
      <div className="min-h-screen bg-stone-50 p-4 flex items-center justify-center relative" data-testid="mobile-login">
        {/* Language Selector - Top Right */}
        <div className="absolute top-4 right-4">
          <LanguageSelector variant="dark" showLabel={false} />
        </div>
        
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <img 
                src="/assets/logo-sally.png" 
                alt="Sally Better" 
                className="h-12 w-auto invert"
              />
            </div>
            <CardTitle className="text-xl">{getLoginTitle()}</CardTitle>
            <p className="text-sm text-stone-500 mt-1">{getLoginSubtitle()}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={language === 'en' ? 'your@email.com' : language === 'pt' ? 'seu@email.com' : 'tu@email.com'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white"
                  data-testid="mobile-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white"
                  data-testid="mobile-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-stone-900 hover:bg-stone-800"
                disabled={isLoggingIn}
                data-testid="mobile-login-btn"
              >
                {getLoginBtnText()}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Mode selection view (after login, before menu selection)
  if (!selectedMode) {
    // Get translated texts based on language
    const getModeTitle = () => {
      switch(language) {
        case 'en': return 'What do you want to do?';
        case 'pt': return 'O que você deseja fazer?';
        case 'ht': return 'Kisa ou vle fè?';
        default: return '¿Qué deseas hacer?';
      }
    };
    
    const getSelectOptionText = () => {
      switch(language) {
        case 'en': return 'Select an option';
        case 'pt': return 'Selecione uma opção';
        case 'ht': return 'Chwazi yon opsyon';
        default: return 'Selecciona una opción';
      }
    };
    
    const getEditorTitle = () => {
      switch(language) {
        case 'en': return 'Edit or Design Menu';
        case 'pt': return 'Editar ou Desenhar Menu';
        case 'ht': return 'Modifye oswa Desine Meni';
        default: return 'Editar o Diseñar Menú';
      }
    };
    
    const getEditorDesc = () => {
      switch(language) {
        case 'en': return 'Full editor with design tools, images, widgets and more';
        case 'pt': return 'Editor completo com ferramentas de design, imagens, widgets e mais';
        case 'ht': return 'Editè konplè ak zouti konsepsyon, imaj, widgèt ak plis';
        default: return 'Editor completo con herramientas de diseño, imágenes, widgets y más';
      }
    };
    
    const getPriceTitle = () => {
      switch(language) {
        case 'en': return 'Price Editor';
        case 'pt': return 'Editor de Preços';
        case 'ht': return 'Editè Pri';
        default: return 'Editor de Precios';
      }
    };
    
    const getPriceDesc = () => {
      switch(language) {
        case 'en': return 'Quick edit for texts and prices from your phone';
        case 'pt': return 'Edição rápida de textos e preços do seu celular';
        case 'ht': return 'Modifikasyon rapid tèks ak pri soti nan telefòn ou';
        default: return 'Edición rápida de textos y precios desde tu celular';
      }
    };

    return (
      <div className="min-h-screen bg-stone-100" data-testid="mobile-mode-selection">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo-sally.png" 
                alt="Sally Better" 
                className="h-8 w-auto"
                style={{ filter: 'invert(1)' }}
              />
              <div>
                <h1 className="font-semibold text-stone-900 text-base">Sally Better</h1>
                <p className="text-xs text-stone-500">{getSelectOptionText()}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <LanguageSelector variant="dark" showLabel={false} />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="p-2 text-stone-400"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mode selection cards */}
        <div className="p-5 pt-10">
          <h2 className="text-center text-xl font-semibold text-stone-800 mb-8">
            {getModeTitle()}
          </h2>
          
          <div className="space-y-4 max-w-md mx-auto">
            {/* Option 1: Edit or design menu (web editor) */}
            <Card 
              className="border-0 shadow-sm bg-white cursor-pointer hover:shadow-md transition-all active:scale-[0.98]"
              onClick={() => {
                // Navigate to web editor (desktop version)
                window.location.href = `${window.location.origin}/dashboard`;
              }}
              data-testid="mobile-mode-editor"
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/20">
                    <Monitor className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900 text-lg">
                      {getEditorTitle()}
                    </p>
                    <p className="text-sm text-stone-500 mt-1 leading-snug">
                      {getEditorDesc()}
                    </p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-emerald-500 shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Option 2: Price editor (mobile optimized) */}
            <Card 
              className="border-0 shadow-sm bg-white cursor-pointer hover:shadow-md transition-all active:scale-[0.98]"
              onClick={() => setSelectedMode('prices')}
              data-testid="mobile-mode-prices"
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-lg shadow-orange-500/20">
                    <DollarSign className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900 text-lg">
                      {getPriceTitle()}
                    </p>
                    <p className="text-sm text-stone-500 mt-1 leading-snug">
                      {getPriceDesc()}
                    </p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-amber-500 shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Menu selection view
  if (!selectedMenu) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="mobile-menu-list">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedMode(null)}
                className="p-2"
                data-testid="mobile-back-to-mode"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-stone-900 text-sm">
                  {mt('selectMenu')}
                </h1>
                <p className="text-xs text-stone-500">{menus.length} {mt('menuCount')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <LanguageSelector variant="dark" showLabel={false} />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="p-2 text-stone-500"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Menu list */}
        <div className="p-4 space-y-3">
          {isLoadingMenus ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-5 bg-stone-200 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-stone-100 rounded w-1/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : menus.length > 0 ? (
            menus.map((menu) => (
              <Card 
                key={menu.id} 
                className="border-stone-200 cursor-pointer hover:bg-stone-50 transition-colors active:bg-stone-100"
                onClick={() => handleSelectMenu(menu)}
                data-testid={`mobile-select-menu-${menu.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-md bg-stone-800 flex items-center justify-center overflow-hidden shrink-0"
                        style={menu.background_image ? {
                          backgroundImage: `url(${menu.background_image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        } : {}}
                      >
                        {!menu.background_image && (
                          <Image className="w-5 h-5 text-stone-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-900 truncate">{menu.name}</p>
                        <p className="text-xs text-stone-500">
                          {menu.items?.length || 0} {mt('texts')}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-dashed border-stone-300">
              <CardContent className="py-8 text-center">
                <img 
                  src="/assets/logo-sally.png" 
                  alt="Sally Better" 
                  className="h-8 w-auto mx-auto opacity-30 mb-2"
                />
                <p className="text-stone-500">{mt('noMenusCreated')}</p>
                <p className="text-xs text-stone-400 mt-1">{mt('createMenuFromComputer')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Edit items view
  return (
    <div className="min-h-screen bg-stone-50" data-testid="mobile-edit-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleBackToMenus}
              className="p-2"
              data-testid="mobile-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-stone-900 text-sm">{selectedMenu.name}</h1>
              <p className="text-xs text-stone-500">{selectedMenu.items?.length || 0} {mt('texts')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="dark" showLabel={false} />
            <Button
              size="sm"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="bg-stone-900 hover:bg-stone-800"
              data-testid="mobile-save-all-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? "..." : mt('save')}
            </Button>
          </div>
        </div>
        
        {/* Search bar */}
        {selectedMenu.items?.length > 0 && (
          <div className="px-4 py-2 border-t border-stone-100 bg-stone-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="text"
                placeholder={mt('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 bg-white border-stone-200 h-9 text-sm"
                data-testid="mobile-search-input"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchQuery("")}
                  data-testid="mobile-search-clear"
                >
                  <X className="w-4 h-4 text-stone-400" />
                </Button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-stone-500 mt-1">
                {filteredItems.length} {mt('resultsOf')} {selectedMenu.items.length} {mt('results')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Items list */}
      <div className="p-4 space-y-3 pb-20">
        {selectedMenu.items?.length > 0 ? (
          filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <Card 
                key={item.id} 
                className="border-stone-200"
                data-testid={`mobile-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-full ${item.type === 'name' ? 'bg-blue-100' : 'bg-green-100'}`}>
                        {item.type === "name" ? 
                          <Type className="w-4 h-4 text-blue-600" /> : 
                          <DollarSign className="w-4 h-4 text-green-600" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-900 truncate">{item.label || item.text}</p>
                        <p className="text-xs text-stone-400">
                          {item.label ? `"${item.text}"` : (item.type === "name" ? mt('name') : mt('price'))}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditItem(item)}
                      className="ml-2 shrink-0"
                      data-testid={`mobile-edit-item-${item.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-dashed border-stone-300">
              <CardContent className="py-8 text-center">
                <Search className="w-8 h-8 mx-auto text-stone-300 mb-2" />
                <p className="text-stone-500">{mt('noResultsFound')}</p>
                <p className="text-xs text-stone-400 mt-1">{mt('tryAnotherSearch')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => setSearchQuery("")}
                >
                  {mt('clearSearch')}
                </Button>
              </CardContent>
            </Card>
          )
        ) : (
          <Card className="border-dashed border-stone-300">
            <CardContent className="py-8 text-center">
              <Type className="w-8 h-8 mx-auto text-stone-300 mb-2" />
              <p className="text-stone-500">{mt('noTextsInMenu')}</p>
              <p className="text-xs text-stone-400 mt-1">{mt('addTextsFromEditor')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <DialogContent className="max-w-[calc(100vw-32px)] rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingItem?.type === "name" ? 
                <Type className="w-5 h-5 text-blue-600" /> : 
                <DollarSign className="w-5 h-5 text-green-600" />
              }
              {mt('edit')} {editingItem?.label || (editingItem?.type === "name" ? mt('name') : mt('price'))}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder={editingItem?.type === "name" ? mt('productName') : mt('price')}
              className="bg-stone-50 text-lg"
              autoFocus
              data-testid="mobile-edit-text-input"
            />
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEditingItem(null)}
              className="flex-1"
            >
              {mt('cancel')}
            </Button>
            <Button 
              onClick={handleSaveItem}
              className="flex-1 bg-stone-900 hover:bg-stone-800"
              data-testid="mobile-confirm-edit-btn"
            >
              <Check className="w-4 h-4 mr-1" />
              {mt('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer info */}
      <div className="fixed bottom-0 left-0 right-0 bg-stone-100 border-t border-stone-200 px-4 py-2">
        <p className="text-xs text-stone-500 text-center">
          {mt('changesWillReflect')}
        </p>
      </div>
    </div>
  );
};

export default MobileEditPage;
